import React, { useEffect, useState } from 'react';
import { Form, Input, Button, DatePicker, Select, InputNumber, Upload, message, Card, Spin, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getUsersList, clearUsersList } from '../../store/slices/userSlice';
import { getJobById, getSlotAvailability, jobupdated, removeImageFromEditJob, updateJob, updateJobStatus } from '../../store/slices/jobSlice';
import { useCategories } from '../../hooks/useCategories';
import { fetchSubCategories, fetchSubServices } from '../../store/slices/subCategorySlice';
import { fetchServices } from '../../store/slices/servicesSlice';
import { useNavigate, useParams } from 'react-router-dom';

const EditJob = () => {
    const { id } = useParams();
    const navigate = useNavigate()
    const [form] = Form.useForm();
    const servicesWatch = Form.useWatch("services", form);
    const dispatch = useDispatch();
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');
    const job = useSelector((state) => state?.jobs)
    const jobData = job?.jobData?.job
    const [fileList, setFileList] = useState([]);
    const [subCategoriesPerRow, setSubCategoriesPerRow] = useState({});
    const [servicesPerRow, setServicesPerRow] = useState({});
    const [subservicesPerRow, setSubServicesPerRow] = useState({});
    const [timeSlots, setTimeSlots] = useState([]);
    const [slotLoading, setSlotLoading] = useState(false);
    const [servicePrices, setServicePrices] = useState({});

    // console.log(fileList, 'fileList')
    // console.log(jobData, 'jobData')


    const { usersList, loading } = useSelector((state) => state?.user);
    const { categories, fetchCategories } = useCategories();

    useEffect(() => {
        dispatch(getJobById(id))
    }, [dispatch, id])
    useEffect(() => {
        fetchCategories();
        if (jobData) {
            form.setFieldsValue({
                // user_id: jobData?.user_id?.first_name,
                user_id: {
                    label: jobData?.user_id?.first_name,
                    value: jobData?.user_id?._id,
                },
                job_date: jobData?.job_date ? dayjs(jobData?.job_date) : null,
                time_slot: jobData?.time_slot,
                payment_method: jobData?.payment_method,
                categorization: jobData?.categorization,
                details: jobData?.details,
                address: jobData?.delivery_address?.address,
                area_code: jobData?.delivery_address?.area_code,
                full_name: jobData?.delivery_address?.full_name,
                region: jobData?.delivery_address?.region,
                city: jobData?.delivery_address?.city,
                phone: jobData?.delivery_address?.phone,
                label: jobData?.delivery_address?.label,
                cost: jobData?.cost,
            });
            // Handle images - support both fileList and direct array formats
            // Ensure we always have an array
            let images = [];
            if (jobData?.images) {
                if (Array.isArray(jobData.images)) {
                    images = jobData.images;
                } else if (jobData.images?.fileList && Array.isArray(jobData.images.fileList)) {
                    images = jobData.images.fileList;
                }
            }

            const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

            setFileList(images.map((img, idx) => {
                // Handle both string paths and object formats
                let imageUrl;

                if (typeof img === 'string') {
                    // If img is already a string path, use it directly
                    imageUrl = img;
                } else if (img && typeof img === 'object') {
                    // If img is an object, try to extract URL from various properties
                    imageUrl = img?.thumbUrl || img?.url || img?.image || img?.imageUrl || img?.path || img?.src;
                }

                // If URL is relative, prepend base URL
                if (imageUrl && typeof imageUrl === 'string' && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
                    // Normalize baseURL - remove trailing slash
                    const normalizedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;

                    // Ensure imagePath starts with /
                    let imagePath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;

                    // If path doesn't start with /uploads/, /images/, or /public/, prepend /uploads
                    if (!imagePath.startsWith('/uploads/') && !imagePath.startsWith('/images/') && !imagePath.startsWith('/public/')) {
                        imagePath = `/uploads${imagePath}`;
                    }

                    // Join baseURL and imagePath (both normalized)
                    imageUrl = `${normalizedBaseURL}${imagePath}`;
                }

                return {
                    uid: typeof img === 'object' ? (img?.uid || img?._id || `existing-${idx}`) : `existing-${idx}-${imageUrl || idx}`,
                    name: typeof img === 'object' ? (img?.name || `image_${idx + 1}.png`) : `image_${idx + 1}.png`,
                    status: 'done',
                    url: imageUrl,
                    thumbUrl: imageUrl,
                };
            }))
        }
        if (!jobData?.services?.length) return;
        const loadDependenciesAndSetValues = async () => {
            const servicesData = [];

            for (let index = 0; index < jobData.services.length; index++) {
                const srv = jobData.services[index];

                if (srv.category) {
                    const subRes = await dispatch(fetchSubCategories(srv.category?._id));
                    setSubCategoriesPerRow(prev => ({
                        ...prev,
                        [index]: subRes.payload || [],
                    }));
                }

                if (srv.category && srv.subCategory) {
                    const servRes = await dispatch(
                        fetchServices({
                            categoryId: srv.category?._id,
                            subCategoryId: srv.subCategory?._id,
                        })
                    );
                    setServicesPerRow(prev => ({
                        ...prev,
                        [index]: servRes.payload?.data || [],
                    }));
                }

                if (srv.sub_service) {
                    const subRes = await dispatch(fetchSubServices(srv.service_type?._id));
                    const subServices = subRes.payload || [];
                    setSubServicesPerRow(prev => ({
                        ...prev,
                        [index]: subServices,
                    }));

                    const servicesList = servicesPerRow[index] || [];

                    const selectedService = servicesList.find(
                        s => s._id === srv.service_type?._id
                    );

                    setServicePrices(prev => ({
                        ...prev,
                        [index]: selectedService?.price || 0
                    }));
                }

                servicesData.push({
                    category: srv.category?._id,
                    subCategory: srv.subCategory?._id,
                    service_type: srv.service_type?._id,
                    sub_service: srv.sub_service?._id,
                    quantity: srv.quantity,
                });
            }

            form.setFieldsValue({
                services: servicesData,
            });
        };

        loadDependenciesAndSetValues();
    }, [jobData, dispatch, form]);

    const handleSearch = (value) => {
        if (value.length === 0) dispatch(clearUsersList());
        if (value.length < 3) return;
        dispatch(getUsersList({ roleType: 'user', search: value }));
    };

    const onClear = () => {
        dispatch(clearUsersList());
    };

    const handleUploadChange = ({ fileList }) => {
        setFileList(fileList.slice(-10)); // optional limit
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return Upload.LIST_IGNORE;
        }
        return false; // prevent auto upload
    };

    const handleFinish = async (values) => {
        setButtonLoading(true);
        try {
            const formattedDate = values.job_date.format('YYYY-MM-DD');

            // Create FormData for file uploads
            const formData = new FormData();

            // Append all non-file fields
            formData.append('_id', id);
            formData.append('job_date', formattedDate);
            formData.append('time_slot', values?.time_slot || '');
            formData.append('payment_method', values?.payment_method || '');
            formData.append('categorization', values?.categorization || '');
            formData.append('details', values?.details || '');
            // formData.append('cost', values?.cost || '');
            formData.append("cost", calculateTotalCost());
            formData.append('user_id', values?.user_id?.value || '');

            // Append delivery address fields with bracket notation (common FormData format)
            formData.append('delivery_address[full_name]', values?.full_name || '');
            formData.append('delivery_address[phone]', values?.phone || '');
            formData.append('delivery_address[region]', values?.region || '');
            formData.append('delivery_address[city]', values?.city || '');
            formData.append('delivery_address[area_code]', values?.area_code || '');
            formData.append('delivery_address[address]', values?.address || '');
            formData.append('delivery_address[label]', values?.label || '');
            formData.append('delivery_address[default_address]', '1');

            // Handle services - use bracket notation like AddJob
            if (values?.services && Array.isArray(values.services)) {
                values.services.forEach((service, index) => {
                    formData.append(`services[${index}][category]`, service.category);
                    formData.append(`services[${index}][subCategory]`, service.subCategory);
                    formData.append(`services[${index}][service_type]`, service.service_type);
                    formData.append(`services[${index}][sub_service]`, service.sub_service);
                    formData.append(`services[${index}][quantity]`, service.quantity);
                });
            }

            // Handle images - only send newly selected images (File objects)
            fileList.forEach((file) => {
                // Only append new file uploads (files with originFileObj)
                if (file.originFileObj && file.originFileObj instanceof File) {
                    formData.append('images', file.originFileObj, file.originFileObj.name);
                }
            });

            // Debug: Log FormData contents

            for (let pair of formData.entries()) {

            }

            const resultAction = await dispatch(jobupdated(formData));

            if (jobupdated.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction.payload?.message || 'Job updated successfully');
                setAlertType('success');
                setTimeout(() => {
                    navigate('/jobs');
                }, 1000);
            } else if (jobupdated.rejected.match(resultAction)) {
                const errorMsg = resultAction.payload || resultAction.error?.message || 'Error updating job';
                setAlertMessage(errorMsg);
                setAlertType('error');
                console.error('Job update error:', resultAction);
            }
        } catch (err) {
            console.error('Job update exception:', err);
            setAlertMessage(err?.message || 'Failed to update job.');
            setAlertType('error');
        } finally {
            setButtonLoading(false);
        }
    };
    const handleCategoryChange = (categoryId, rowIndex) => {
        dispatch(fetchSubCategories(categoryId)).then((res) => {
            setSubCategoriesPerRow((prev) => ({ ...prev, [rowIndex]: res.payload }));
            setServicesPerRow((prev) => ({ ...prev, [rowIndex]: [] }));
        });
    };

    const handleSubCategoryChange = (subCategoryId, rowIndex, categoryId) => {
        dispatch(fetchServices({ categoryId, subCategoryId })).then((res) => {
            setServicesPerRow((prev) => ({ ...prev, [rowIndex]: res.payload.data }));
        });
    };

    const handleJobDateChange = async (date) => {
        if (!date) return;

        const dayName = date.format('ddd');

        try {
            setSlotLoading(true);

            const res = await dispatch(
                getSlotAvailability({
                    day: dayName,
                })
            );

            setTimeSlots(res?.payload?.data || []);

            // reset previously selected slot
            form.setFieldsValue({ time_slot: undefined });

        } catch (err) {
            setAlertMessage('Failed to load time slots');
            setAlertType('error');
        } finally {
            setSlotLoading(false);
        }
    };

    const calculateRowTotal = (index) => {
        const qty = servicesWatch?.[index]?.quantity || 0;
        const price = servicePrices[index] || 0;
        return qty * price;
    };

    const calculateTotalCost = () => {
        if (!servicesWatch) return 0;
        return servicesWatch.reduce((sum, _, index) => {
            const qty = servicesWatch[index]?.quantity || 0;
            const price = servicePrices[index] || 0;
            return sum + qty * price;
        }, 0);
    };

    const handleRemove = async (file) => {
        try {
            dispatch(removeImageFromEditJob({ file, id }))
            return true;
        } catch (error) {
            console.log(error)
            return false;
        }
    };
    

    return (
        <Card title="Edit Job" variant="outlined">
            {alertMessage && (
                <Alert
                    message={alertMessage}
                    type={alertType}
                    showIcon
                    icon={alertType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    closable
                    onClose={() => setAlertMessage('')}
                    style={{ marginBottom: 24, borderRadius: '8px' }}
                />
            )}

            <Form
                layout="vertical"
                form={form}
                onFinish={handleFinish}
            >
                <Form.Item
                    label="User"
                    name="user_id"
                    rules={[{ required: true, message: 'Please select user' }]}
                >
                    <Select labelInValue
                        showSearch
                        allowClear
                        placeholder="Type at least 3 characters to search"
                        filterOption={false}
                        onSearch={handleSearch}
                        notFoundContent={loading ? <Spin size="small" /> : null}
                        onClear={onClear}
                        options={(usersList?.data || []).map((user) => ({
                            value: user._id,
                            label: `${user.first_name} ${user.last_name}`,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Job Date"
                    name="job_date"
                    rules={[{ required: true, message: 'Please select job date' }]}
                >
                    <DatePicker style={{ width: '100%' }} onChange={handleJobDateChange} />
                </Form.Item>

                <Form.Item
                    label="Time Slot"
                    name="time_slot"
                    rules={[{ required: true, message: 'Please select time slot' }]}
                >
                    <Select
                        placeholder="Select time slot"
                        options={(timeSlots?.[0]?.time_slot || []).map((data) => ({
                            value: `${data.start_time}-${data.end_time}`,
                            label: `${data.start_time} - ${data.end_time}`,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Payment Method"
                    name="payment_method"
                    rules={[{ required: true, message: 'Please select payment method' }]}
                >
                    <Select
                        placeholder="Select payment method"
                        options={[
                            { value: 'card', label: 'Card' },
                            { value: 'cash', label: 'Cash' },
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    label="Categorization"
                    name="categorization"
                    rules={[{ required: true, message: 'Please select categorization' }]}
                >
                    <Select
                        placeholder="Select categorization"
                        options={[
                            { value: 'simple', label: 'Simple' },
                            { value: 'complex', label: 'Complex' },
                        ]}
                    />
                </Form.Item>

                <Form.List name="services">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <Card key={field.key} style={{ marginBottom: 20, background: '#fafafa' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <h4>Service #{index + 1}</h4>
                                        {index > 0 && <Button danger onClick={() => remove(field.name)}>Remove</Button>}
                                    </div>

                                    <Form.Item
                                        key={`${field.key}-category`}
                                        label="Category"
                                        name={[field.name, 'category']}
                                        rules={[{ required: true, message: 'Select category' }]}
                                    >
                                        <Select
                                            placeholder="Select category"
                                            options={(categories || []).map((cat) => ({
                                                key: cat._id,
                                                value: cat._id,
                                                label: cat.name,
                                            }))}
                                            onChange={(catId) => handleCategoryChange(catId, index)}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        key={`${field.key}-subCategory`}
                                        label="Sub Category"
                                        name={[field.name, 'subCategory']}
                                        rules={[{ required: true, message: 'Select sub category' }]}
                                    >
                                        <Select
                                            placeholder="Select sub category"
                                            options={(subCategoriesPerRow?.[index] || []).map((sub) => ({
                                                key: sub._id,
                                                value: sub._id,
                                                label: sub.name,
                                            }))}
                                            onChange={(subId) =>
                                                handleSubCategoryChange(subId, index, form.getFieldValue(['services', index, 'category']))
                                            }
                                            disabled={!subCategoriesPerRow?.[index]?.length}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        key={`${field.key}-service_type`}
                                        label="Service"
                                        name={[field.name, 'service_type']}
                                        rules={[{ required: true, message: 'Select service' }]}
                                    >
                                        <Select
                                            placeholder="Select service"
                                            options={(servicesPerRow?.[index] || []).map((s) => ({
                                                value: s._id,
                                                label: s.title,
                                                price: s.price,
                                            }))}
                                             onChange={(value, option) => {
                                                const service = servicesPerRow[index].find(s => s._id === value);
                                                console.log(service, 'serv')
                                                setServicePrices(prev => ({
                                                    ...prev,
                                                    [index]: service?.price || 0
                                                }));
                                                dispatch(fetchSubServices(service?._id)).then((res) => {
                                                    setSubServicesPerRow((prev) => ({
                                                        ...prev,
                                                        [index]: res.payload
                                                    }));
                                                    // setServicesPerRow((prev) => ({
                                                    //     ...prev,
                                                    //     [index]: []
                                                    // }));
                                                });
                                            }}
                                            disabled={!servicesPerRow?.[index]?.length}
                                        />
                                    </Form.Item>

                                    {/* SUB SERVICE */}
                                    <Form.Item
                                        {...field}
                                        label="Sub Service"
                                        name={[field.name, "sub_service"]}
                                        fieldKey={[field.fieldKey, "sub_service"]}
                                        rules={[{ required: true, message: "Select sub service" }]}
                                    >
                                        <Select
                                        placeholder="Select sub service"
                                        options={(subservicesPerRow[index] || []).map((s) => ({
                                            value: s._id,
                                            label: s.name,
                                            price: s.cost,
                                        }))}
                                        // onChange={(value, option) => {
                                        //     const service = subservicesPerRow[index].find(s => s._id === value);
                                        //     setServicePrices(prev => ({
                                        //         ...prev,
                                        //         [index]: service?.price || 0
                                        //     }));
                                        // }}
                                        disabled={!subservicesPerRow[index]?.length}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        key={`${field.key}-quantity`}
                                        label="Quantity"
                                        name={[field.name, 'quantity']}
                                        rules={[{ required: true, message: 'Enter quantity' }]}
                                    >
                                        <InputNumber placeholder="Enter quantity" style={{ width: '100%' }} />
                                    </Form.Item>
                                    Sub Total: ${calculateRowTotal(index)}
                                </Card>
                            ))}
                            <Button type="dashed" onClick={() => add()} block>
                                + Add Another Service
                            </Button>
                        </>
                    )}
                </Form.List>

                <Form.Item
                    label="Details"
                    name="details"
                    rules={[{ required: true, message: 'Please enter details' }]}
                >
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item
                    label="Full Name"
                    name="full_name"
                    rules={[{ required: true, message: 'Please enter full name' }]}
                >
                    <Input placeholder="Enter full name" />
                </Form.Item>

                <Form.Item
                    label="Phone"
                    name="phone"
                    rules={[{ required: true, message: 'Please enter phone' }]}
                >
                    <InputNumber placeholder="Enter phone" style={{ width : "100%" }} />
                </Form.Item>

                <Form.Item
                    label="Region"
                    name="region"
                    rules={[{ required: true, message: 'Please enter region' }]}
                >
                    <Input placeholder="Enter region" />
                </Form.Item>

                <Form.Item
                    label="City"
                    name="city"
                    rules={[{ required: true, message: 'Please enter city' }]}
                >
                    <Input placeholder="Enter city" />
                </Form.Item>

                <Form.Item
                    label="Area Code"
                    name="area_code"
                    rules={[{ required: true, message: 'Please enter area code' }]}
                >
                    <Input placeholder="Enter area code" />
                </Form.Item>

                <Form.Item
                    label="Address"
                    name="address"
                    rules={[{ required: true, message: 'Please enter address' }]}
                >
                    <Input.TextArea placeholder="Enter address" rows={4} />
                </Form.Item>

                <Form.Item
                    label="Label"
                    name="label"
                    rules={[{ required: true, message: 'Please enter label' }]}
                >
                    <Input placeholder="Enter label" />
                </Form.Item>

                {/* <Form.Item
                    label="Cost"
                    name="cost"
                    rules={[{ required: true, message: 'Please enter cost' }]}
                >
                    <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder="Enter cost" prefix="$" />
                </Form.Item> */}

                <Form.Item
                    label="Images"
                    name="images"
                // rules={[{ required: true, message: 'Please upload at least one image' }]}
                >
                    <Upload
                        beforeUpload={beforeUpload}
                        fileList={fileList}
                        onChange={handleUploadChange}
                        onRemove={handleRemove}
                        accept="image/*"
                        multiple
                        listType="picture-card"
                        showUploadList={{
                            showPreviewIcon: true,
                            showRemoveIcon: true,
                        }}
                    >
                        {fileList.length < 10 && (
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={buttonLoading} style={{ marginTop: 16 }}>
                        Update Job
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default EditJob;
